<template>
  <!-- Class and style to the box, everything else to the control: same split as
       UiInput, for the same reason. -->
  <div
    class="textarea"
    :class="[$attrs.class, { 'textarea--invalid': invalid }]"
    :style="$attrs.style"
  >
    <textarea
      v-bind="controlAttrs"
      :id="controlId"
      :value="modelValue"
      :rows="rows"
      :maxlength="maxlength"
      :aria-invalid="invalid || undefined"
      class="textarea__el"
      @input="$emit('update:modelValue', $event.target.value)"
    />
    <!-- A limit the user cannot see is a limit they hit by surprise. -->
    <span v-if="maxlength" class="textarea__count" :class="{ 'textarea__count--near': near }">
      {{ (modelValue ?? '').length }}/{{ maxlength }}
    </span>
  </div>
</template>

<script setup>
import { computed, inject, useAttrs } from 'vue'

import { FIELD_ID } from '../utils/fieldId'

defineOptions({ inheritAttrs: false })
const props = defineProps({
  modelValue: { type: String, default: '' },
  rows: { type: Number, default: 3 },
  maxlength: Number,
  id: String,
  invalid: Boolean,
})

// El id que pone la etiqueta del campo, cuando el llamante no ha dado uno.
const fieldId = inject(FIELD_ID, null)
const controlId = computed(() => props.id ?? fieldId)
defineEmits(['update:modelValue'])

const attrs = useAttrs()
const controlAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

const near = computed(() =>
  Boolean(props.maxlength) && (props.modelValue ?? '').length > props.maxlength * 0.9
)
</script>

<style scoped>
.textarea {
  position: relative;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  transition: border-color var(--fast), box-shadow var(--fast);
}
.textarea:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-ring);
}
.textarea--invalid { border-color: var(--color-danger); }

.textarea__el {
  display: block;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  outline: none;
  background: transparent;
  font-size: var(--text-field);
  line-height: 1.55;
  resize: vertical;
}
.textarea__el::placeholder { color: var(--color-text-mute); }

.textarea__count {
  position: absolute;
  right: var(--space-2);
  bottom: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-text-mute);
  pointer-events: none;
}
.textarea__count--near { color: var(--color-warning); }
</style>
